<script>
  import { router, useForm } from '@inertiajs/svelte'
  import { liveValidate } from '../../lib/live_validation.svelte.js'
  import Layout from '../../layouts/layout.svelte'
  import PageHeader from '../../components/page_header.svelte'
  import FormLayout from '../../components/form_layout.svelte'
  import FormSection from '../../components/form_section.svelte'
  import ScheduleEditor from '../../components/schedule_editor.svelte'
  import MultiSelect from '../../components/multi_select.svelte'
  import ImageSelector from '../../components/image_selector.svelte'
  import GoogleMap from '../../components/google_map.svelte'
  import PlaceSearch from '../../components/place_search.svelte'
  import TextField from '../../components/form/text_field.svelte'
  import TextareaField from '../../components/form/textarea_field.svelte'
  import SelectField from '../../components/form/select_field.svelte'
  import ToggleField from '../../components/form/toggle_field.svelte'
  import NumberField from '../../components/form/number_field.svelte'
  import SubmitButton from '../../components/form/submit_button.svelte'
  import ShrineImageField from '../../components/shrine_attachment_field.svelte'
  import { Button } from '../../components/ui'

  let { place, back_url: backUrl = '/places', show_apartment_fields: showApartmentFields = false, options = {} } = $props()

  const isEdit = !!place.id
  const url = isEdit ? `/places/${place.id}` : '/places'

  // The server hands over form-ready values, validates every change live (Precognition), and
  // shapes the submit (AdminSite::PlacesController#place_attributes). Nested under `place` with
  // dotted names, no transform: validate('place.name') reads the value at that path.
  const form = useForm({
    place: { ...structuredClone(place.fields), image_ids: place.images.map((image) => image.id), icon: null, remove_icon: false },
    return_to: backUrl
  }).withPrecognition(isEdit ? 'patch' : 'post', url)
  liveValidate(form, 'place')

  let images = $state([...place.images])
  $effect(() => {
    $form.place.image_ids = images.map((image) => image.id)
  })

  // Google picks show their address/phone/hours right away; the server refreshes them on save.
  let picked = $state({})
  const location = $derived({ ...place, ...picked })

  // Whether the Apartment Information section shows is the server's call: re-ask it when the
  // categories change.
  let lastCategories = JSON.stringify($form.place.sub_category_ids)
  $effect(() => {
    const current = JSON.stringify($form.place.sub_category_ids)
    if (current === lastCategories) return
    lastCategories = current
    router.reload({
      only: ['show_apartment_fields'],
      data: { sub_category_ids: $form.place.sub_category_ids.join(',') },
      preserveUrl: true,
      preserveState: true,
      preserveScroll: true
    })
  })
  const error = (key) => $form.errors[`place.${key}`]

  // The schedule's time zone follows the College Town (the server knows each town's zone); the
  // admin can still pick another one afterwards.
  const onAreaChange = () => {
    router.reload({
      only: ['area_time_zone'],
      data: { area_id: $form.place.area_id },
      preserveUrl: true,
      preserveState: true,
      preserveScroll: true,
      onSuccess: (page) => {
        const timeZone = page.props.area_time_zone
        if (timeZone) $form.place.schedule_attributes = { ...$form.place.schedule_attributes, time_zone: timeZone }
      }
    })
  }

  const onMapSelect = ({ placeId, lat, lng, address, hours, phone, website }) => {
    $form.place.latitude = lat
    $form.place.longitude = lng
    if (placeId) $form.place.google_id = placeId
    picked = { address, hours, phone, website_url: website }
  }

  const submit = () => {
    if (isEdit) $form.patch(url)
    else $form.post(url)
  }
</script>

<Layout>
  <PageHeader title={isEdit ? 'Edit Place' : 'New Place'} subtitle="Check-in venues within each college town" />

  <FormLayout
    onsubmit={(event) => {
      event.preventDefault()
      submit()
    }}
  >
    <FormSection title="Details">
      <div class="space-y-4">
        <TextField bind:value={$form.place.name} label="Name" name="name" error={error('name')} />
        <TextField bind:value={$form.place.display_name} label="Display Name" name="display_name" help="Displayed in the app" error={error('display_name')} />
        <TextareaField bind:value={$form.place.description} label="Description" name="description" error={error('description')} />
        <ToggleField bind:checked={$form.place.active} label="Status (Active)" name="active" help="If this place is active or not." error={error('active')} />
        <ToggleField bind:checked={$form.place.checkin_enabled} label="Check-in" name="checkin_enabled" error={error('checkin_enabled')} />
        <ToggleField bind:checked={$form.place.official_partner} label="Official Partner" name="official_partner" error={error('official_partner')} />
        <SelectField bind:value={$form.place.point_source_id} label="Point Source" name="point_source_id" options={options.point_sources} placeholder={null} error={error('point_source_id')} />
        <SelectField bind:value={$form.place.moment_point_source_id} label="Moment Point Source" name="moment_point_source_id" options={options.point_sources} placeholder={null} error={error('moment_point_source_id')} />
        <SelectField
          bind:value={$form.place.area_id}
          label="College Town"
          name="area_id"
          options={options.areas}
          onchange={onAreaChange}
          error={error('area_id')}
        />
        <MultiSelect bind:value={$form.place.sub_category_ids} options={options.sub_categories} label="Categories" placeholder="Select a category…" />
      </div>
    </FormSection>

    {#if showApartmentFields}
      <FormSection title="Apartment Information">
        <div class="space-y-4">
          <TextField bind:value={$form.place.visit_us_url} label="Visit Us URL" name="visit_us_url" help="URL for visitors to learn more about this apartment" error={error('visit_us_url')} />
          <TextField bind:value={$form.place.book_tour_url} label="Book Tour URL" name="book_tour_url" help="URL for visitors to book a tour of this apartment" error={error('book_tour_url')} />
        </div>
      </FormSection>
    {/if}

    <FormSection title="Location">
      <div class="space-y-4">
        <ToggleField bind:checked={$form.place.google_auto_update} label="Google Auto Update" name="google_auto_update" help="Update this place's schedule and address information once weekly automatically." />
        <TextField bind:value={$form.place.google_id} label="Google ID" name="google_id" help="Paste a Google Place ID, or search below to fill in latitude/longitude." error={error('google_id')} />

        <fieldset class="fieldset">
          <legend class="fieldset-legend">Search for a place</legend>
          <PlaceSearch onselect={onMapSelect} />
        </fieldset>

        <GoogleMap bind:lat={$form.place.latitude} bind:lng={$form.place.longitude} onchange={({ lat, lng }) => { $form.place.latitude = lat; $form.place.longitude = lng }} />

        <div class="flex gap-4">
          <NumberField bind:value={$form.place.latitude} label="Latitude" name="latitude" step="0.0001" error={error('latitude')} />
          <NumberField bind:value={$form.place.longitude} label="Longitude" name="longitude" step="0.0001" error={error('longitude')} />
        </div>
        <NumberField bind:value={$form.place.radius} label="Checkin Radius (meters)" name="radius" min={0} error={error('radius')} />

        <div class="text-sm space-y-1">
          {#if location.google_maps_name}<p><span class="font-semibold">Google Name:</span> {location.google_maps_name}</p>{/if}
          <p><span class="font-semibold">Address:</span> {location.address || 'No address yet. Assign a Google ID.'}</p>
          {#if location.phone}<p><span class="font-semibold">Phone:</span> <a class="link" href={`tel:${location.phone}`}>{location.phone}</a></p>{/if}
          {#if location.website_url}<p><span class="font-semibold">Website:</span> <a class="link" href={location.website_url} target="_blank" rel="noreferrer">{location.website_url}</a></p>{/if}
          {#if location.hours?.length}
            <div>
              <span class="font-semibold">Hours:</span>
              {#each location.hours as line (line)}<p>{line}</p>{/each}
            </div>
          {/if}
        </div>
      </div>
    </FormSection>

    <FormSection title="Active Schedule">
      <ScheduleEditor bind:value={$form.place.schedule_attributes} timeZones={options.time_zones ?? []} error={error('schedule')} />
    </FormSection>

    <FormSection title="Icon">
      <ShrineImageField bind:value={$form.place.icon} bind:remove={$form.place.remove_icon} previewUrl={place.icon_url} label="Icon" />
      {#if error('icon')}<p class="text-sm text-error">{error('icon')}</p>{/if}
    </FormSection>

    <FormSection title="Images">
      <ImageSelector bind:value={images} placeId={place.id} />
    </FormSection>

    {#if error('base')}<p class="text-sm text-error">{error('base')}</p>{/if}

    {#snippet footer()}
      <Button href={backUrl} variant="ghost" size="sm">Cancel</Button>
      <SubmitButton processing={$form.processing} label={isEdit ? 'Update' : 'Save'} full={false} />
    {/snippet}
  </FormLayout>
</Layout>
